import type { Problem } from '@/types';

/* Practice Gym ladder: Date / Time Logic (module m7). Problem-forward, progressive.
   Auto-included via src/data/gym/index.ts; referenced by the `dt` ladder. */
export const datetimeProblems: Problem[] = [
  {
    "id": "dt1",
    "ladder": "dt",
    "pos": 1,
    "stage": "Recognition",
    "lvl": 0,
    "difficulty": "recognition",
    "priority": "required",
    "source": "interview-style",
    "module": "m7",
    "title": "Calendar month vs trailing 30 days",
    "concept": ["datetime"],
    "obj": ["Charge"],
    "metric": "n/a",
    "edge": ["calendar vs rolling"],
    "timed": false,
    "est": "3 min",
    "business": "A dashboard labelled 'this month' uses created_at >= NOW() - INTERVAL '30 days'. Finance says the number looks too high mid-month.",
    "schema": ["charges"],
    "prompt": "No SQL yet. In plain English: why is a trailing-30-day filter not the same as 'this calendar month', and how would each be expressed?",
    "deliverable": "A two-sentence explanation an interviewer could follow: the difference and the two expressions.",
    "why": "Confusing rolling windows with calendar periods is the most common date bug; you must recognise it before writing any time metric.",
    "harder": "First rung — name the distinction and both expressions.",
    "before": [
      "Picture today as the 10th: how many days does 'this month' cover vs 'last 30 days'?",
      "Recall DATE_TRUNC('month', …) as the calendar-period tool."
    ],
    "hints": [
      "Trailing 30 days = a moving window ending now: created_at >= NOW() - INTERVAL '30 days'.",
      "Calendar month = DATE_TRUNC('month', created_at) = DATE_TRUNC('month', NOW())."
    ],
    "model": "A trailing 30-day window always ends today and reaches back 30 days, so on the 10th it includes ~20 days of the previous month. 'This calendar month' is only the rows whose month equals the current month. Express rolling as created_at >= NOW() - INTERVAL '30 days', and calendar as DATE_TRUNC('month', created_at) = DATE_TRUNC('month', NOW()). They match only on the 1st.",
    "grain": "Conceptual — no output rows.",
    "confusion": "'Last 30 days' and 'this month' coincide only on the first of the month.",
    "explain": "State the rolling-vs-calendar distinction and both filters aloud.",
    "teaches": "Recognising calendar periods vs rolling windows and their SQL forms.",
    "mode": "SQL"
  },
  {
    "id": "dt2",
    "ladder": "dt",
    "pos": 2,
    "stage": "Mechanical",
    "lvl": 1,
    "difficulty": "easy",
    "priority": "required",
    "source": "pgexercises-style",
    "module": "m7",
    "title": "Bucket charges by day",
    "concept": ["datetime"],
    "obj": ["Charge"],
    "metric": "count",
    "edge": [],
    "timed": false,
    "est": "4 min",
    "business": "You want a per-day count of all charges, not one row per timestamp.",
    "schema": ["charges"],
    "prompt": "Return one row per calendar day with the total charge count, using <code class='inline'>DATE_TRUNC</code>.",
    "deliverable": "One row per day: day, charges.",
    "why": "Truncating a timestamp to a day is the bucketing primitive every time series is built on.",
    "harder": "Introduces DATE_TRUNC and grouping by the truncated value.",
    "before": ["Why GROUP BY created_at directly would give hundreds of buckets."],
    "hints": [
      "DATE_TRUNC('day', created_at) collapses each timestamp to midnight of that day.",
      "GROUP BY the truncated value and ORDER BY it."
    ],
    "solution": "SELECT DATE_TRUNC('day', created_at) AS day,\n       COUNT(*) AS charges\nFROM charges\nGROUP BY DATE_TRUNC('day', created_at)\nORDER BY day;",
    "grain": "One row per calendar day.",
    "confusion": "Group by the truncated bucket, never the raw timestamp.",
    "explain": "Explain that DATE_TRUNC normalises every charge in a day to one bucket key.",
    "teaches": "Bucketing a timestamp to a day with DATE_TRUNC.",
    "mode": "SQL"
  },
  {
    "id": "dt3",
    "ladder": "dt",
    "pos": 3,
    "stage": "Simple application",
    "lvl": 2,
    "difficulty": "easy",
    "priority": "required",
    "source": "Mode-style",
    "module": "m7",
    "title": "Daily succeeded count, last 7 days",
    "concept": ["datetime"],
    "obj": ["Charge"],
    "metric": "count",
    "edge": ["window boundary"],
    "timed": false,
    "est": "5 min",
    "business": "Ops wants a 7-day spark of successful payments.",
    "schema": ["charges"],
    "prompt": "Return one row per day for the last 7 days with the count of succeeded charges.",
    "deliverable": "One row per day (≤ 8 rows): day, succeeded.",
    "why": "Combining a rolling window with daily bucketing is the everyday time-series shape at Stripe.",
    "harder": "Adds a rolling window filter on top of day bucketing.",
    "before": ["Is the window rolling or calendar? Here, rolling 7 days."],
    "hints": [
      "Filter status='succeeded' AND created_at >= NOW() - INTERVAL '7 days'.",
      "Bucket and group by DATE_TRUNC('day', created_at)."
    ],
    "solution": "SELECT DATE_TRUNC('day', created_at) AS day,\n       COUNT(*) AS succeeded\nFROM charges\nWHERE status='succeeded'\n  AND created_at >= NOW() - INTERVAL '7 days'\nGROUP BY DATE_TRUNC('day', created_at)\nORDER BY day;",
    "verify": {
      "grain": "One row per day in the trailing 7-day window.",
      "columns": ["day", "succeeded"],
      "sample": { "cols": ["day", "succeeded"], "rows": [["2024-06-01", "812"], ["2024-06-02", "790"]] },
      "commonWrong": ["Counting all statuses.", "Grouping by raw created_at (too many rows)."],
      "validation": ["At most 8 day rows; counts positive."],
      "edgeCases": ["A day with zero succeeded charges simply won't appear (no gap-filling here).", "Boundary day exactly 7 days ago: >= includes it."],
      "checklist": ["status filter", "rolling window", "DATE_TRUNC('day') bucket"]
    },
    "confusion": "Missing days are absent, not zero — gap-filling needs a calendar/series, which is a later topic.",
    "explain": "State the rolling window and the daily bucket before writing the query.",
    "teaches": "A windowed daily time series.",
    "mode": "SQL"
  },
  {
    "id": "dt4",
    "ladder": "dt",
    "pos": 4,
    "stage": "Applied metric",
    "lvl": 3,
    "difficulty": "medium",
    "priority": "required",
    "source": "DataLemur-style",
    "module": "m7",
    "title": "Monthly GPV by merchant",
    "concept": ["datetime"],
    "obj": ["Charge"],
    "metric": "GPV",
    "edge": ["currency", "month bucket"],
    "timed": false,
    "est": "7 min",
    "business": "Finance wants each merchant's monthly USD GPV for the last 6 months.",
    "schema": ["charges"],
    "prompt": "One row per merchant per month for the last 6 months: succeeded USD GPV.",
    "deliverable": "One row per (merchant, month): merchant_id, month, gpv_usd.",
    "why": "Monthly per-entity rollups are the backbone of Stripe finance reporting.",
    "harder": "Two group keys (merchant and month) plus a multi-month calendar window.",
    "before": ["Scope currency='usd' and status='succeeded'.", "Window: start from the first of the month 6 months ago."],
    "hints": [
      "Group by merchant_id AND DATE_TRUNC('month', created_at).",
      "created_at >= DATE_TRUNC('month', NOW()) - INTERVAL '6 months' keeps whole months."
    ],
    "solution": "SELECT merchant_id,\n       DATE_TRUNC('month', created_at) AS month,\n       SUM(amount)/100.0 AS gpv_usd\nFROM charges\nWHERE status='succeeded' AND currency='usd'\n  AND created_at >= DATE_TRUNC('month', NOW()) - INTERVAL '6 months'\nGROUP BY merchant_id, DATE_TRUNC('month', created_at)\nORDER BY merchant_id, month;",
    "verify": {
      "grain": "One row per merchant per month.",
      "columns": ["merchant_id", "month", "gpv_usd"],
      "sample": { "cols": ["merchant_id", "month", "gpv_usd"], "rows": [["101", "2024-01-01", "48210.50"], ["101", "2024-02-01", "51990.00"]] },
      "commonWrong": ["Grouping by raw created_at.", "Forgetting currency scope and summing mixed currencies.", "Trailing 180 days instead of 6 calendar months."],
      "validation": ["gpv_usd > 0", "≤ 6 month buckets per merchant"],
      "edgeCases": ["A merchant inactive in a month → that month row is absent.", "Partial current month."],
      "checklist": ["merchant + month keys", "currency scoped", "succeeded only", "6-month calendar window"]
    },
    "confusion": "Anchor the window to DATE_TRUNC('month', NOW()) so you get whole months, not a trailing 180-day span.",
    "explain": "Name the two group keys and the currency scope before writing.",
    "teaches": "Per-merchant monthly rollups over a calendar-month window.",
    "mode": "SQL"
  },
  {
    "id": "dt5",
    "ladder": "dt",
    "pos": 5,
    "stage": "Multi-step",
    "lvl": 4,
    "difficulty": "hard",
    "priority": "should",
    "source": "Mode-style",
    "module": "m7",
    "title": "This month vs previous month GPV",
    "concept": ["datetime"],
    "obj": ["Charge"],
    "metric": "GPV change",
    "edge": ["month boundary", "zero denominator"],
    "timed": false,
    "est": "9 min",
    "business": "A merchant scorecard needs current-month USD GPV, previous-month USD GPV, and the % change.",
    "schema": ["charges"],
    "prompt": "Per merchant, return this_month_usd, prev_month_usd and the MoM % change, using a monthly CTE.",
    "deliverable": "One row per merchant: merchant_id, this_month_usd, prev_month_usd, mom_change.",
    "why": "Period-over-period comparison is a constant ask, and the month math is where people slip.",
    "harder": "Buckets to month, then pivots two specific months and guards the divide.",
    "before": ["Both months are calendar periods anchored to DATE_TRUNC('month', NOW()).", "Guard the previous-month denominator with NULLIF."],
    "howto": [
      "CTE monthly: per-merchant monthly gross for this and last month.",
      "Pivot with SUM(...) FILTER (WHERE month = this/prev).",
      "Compute (this - prev)/prev with NULLIF."
    ],
    "hints": [
      "Window to the last two months: created_at >= DATE_TRUNC('month', NOW()) - INTERVAL '1 month'.",
      "this = month = DATE_TRUNC('month', NOW()); prev = that minus INTERVAL '1 month'."
    ],
    "solution": "WITH monthly AS (\n  SELECT merchant_id,\n         DATE_TRUNC('month', created_at) AS month,\n         SUM(amount) AS gross\n  FROM charges\n  WHERE status='succeeded' AND currency='usd'\n    AND created_at >= DATE_TRUNC('month', NOW()) - INTERVAL '1 month'\n  GROUP BY merchant_id, DATE_TRUNC('month', created_at)\n)\nSELECT merchant_id,\n       SUM(gross) FILTER (WHERE month = DATE_TRUNC('month', NOW()))/100.0 AS this_month_usd,\n       SUM(gross) FILTER (WHERE month = DATE_TRUNC('month', NOW()) - INTERVAL '1 month')/100.0 AS prev_month_usd,\n       ROUND(\n         (SUM(gross) FILTER (WHERE month = DATE_TRUNC('month', NOW()))\n          - SUM(gross) FILTER (WHERE month = DATE_TRUNC('month', NOW()) - INTERVAL '1 month'))::numeric\n         / NULLIF(SUM(gross) FILTER (WHERE month = DATE_TRUNC('month', NOW()) - INTERVAL '1 month'),0), 4) AS mom_change\nFROM monthly\nGROUP BY merchant_id\nORDER BY merchant_id;",
    "verify": {
      "grain": "One row per merchant.",
      "columns": ["merchant_id", "this_month_usd", "prev_month_usd", "mom_change"],
      "commonWrong": ["Comparing to 30 days ago instead of the previous calendar month.", "Divide-by-zero when a merchant had no GPV last month."],
      "validation": ["mom_change is NULL only when prev month is 0/absent", "amounts in dollars"],
      "edgeCases": ["A merchant new this month (prev = NULL).", "Partial current month makes the change look negative."],
      "checklist": ["monthly CTE", "pivot with FILTER", "NULLIF guard", "calendar months not rolling"]
    },
    "confusion": "Use FILTER to pivot the two months from one bucketed CTE rather than two separate scans.",
    "explain": "State that both periods are calendar months and how you guard a zero previous month.",
    "teaches": "Period-over-period comparison from a monthly CTE.",
    "mode": "SQL"
  },
  {
    "id": "dt6",
    "ladder": "dt",
    "pos": 6,
    "stage": "Debug",
    "lvl": 3,
    "difficulty": "medium",
    "priority": "should",
    "source": "interview-style",
    "module": "m7",
    "title": "Debug: 'this month' with a trailing window",
    "concept": ["datetime"],
    "obj": ["Charge"],
    "metric": "GPV",
    "edge": ["calendar vs rolling"],
    "timed": false,
    "est": "5 min",
    "business": "A report titled 'This month's GPV' is overstating because it includes part of last month.",
    "schema": ["charges"],
    "prompt": "Diagnose the bug in one sentence, then fix it so the result is the current calendar month only.",
    "broken": "SELECT SUM(amount)/100.0 AS gpv_usd\nFROM charges\nWHERE status='succeeded'\n  AND created_at >= NOW() - INTERVAL '30 days';",
    "deliverable": "A one-sentence diagnosis + a corrected calendar-month query.",
    "why": "Recognising and fixing rolling-as-calendar is the single most common date interview moment.",
    "harder": "You must replace the window type, not tweak the number of days.",
    "before": ["On any day after the 1st, trailing 30 days reaches into the previous month."],
    "hints": [
      "The bug: a rolling 30-day window is not the calendar month.",
      "Fix: DATE_TRUNC('month', created_at) = DATE_TRUNC('month', NOW())."
    ],
    "solution": "SELECT SUM(amount)/100.0 AS gpv_usd\nFROM charges\nWHERE status='succeeded'\n  AND DATE_TRUNC('month', created_at) = DATE_TRUNC('month', NOW());",
    "verify": {
      "grain": "A single scalar (current-month GPV).",
      "columns": ["gpv_usd"],
      "commonWrong": ["Changing '30 days' to '31 days' — still rolling, still wrong.", "Using EXTRACT(month) without EXTRACT(year), mixing years."],
      "validation": ["Result equals GPV for rows whose month = current month", "On the 1st, both versions agree"],
      "edgeCases": ["Year boundary: comparing only month would merge Decembers across years — DATE_TRUNC keeps year too."],
      "checklist": ["named the bug as rolling-vs-calendar", "DATE_TRUNC month equality", "year preserved"]
    },
    "confusion": "Comparing only EXTRACT(month) ignores the year; DATE_TRUNC carries the year and is the safe fix.",
    "explain": "State the rolling-vs-calendar bug before the fix.",
    "teaches": "Converting a rolling filter into a true calendar-month filter.",
    "mode": "SQL"
  },
  {
    "id": "dt7",
    "ladder": "dt",
    "pos": 7,
    "stage": "Edge-case hard",
    "lvl": 4,
    "difficulty": "hard",
    "priority": "stretch",
    "source": "StrataScratch-style",
    "module": "m7",
    "title": "Dispute rate by charge month (not dispute month)",
    "concept": ["datetime"],
    "obj": ["Charge", "Dispute"],
    "metric": "dispute rate",
    "edge": ["late-arriving data", "timestamp choice"],
    "timed": false,
    "est": "10 min",
    "business": "Risk wants dispute rate attributed to the month the CHARGE happened, but disputes are filed weeks later.",
    "schema": ["charges", "disputes"],
    "prompt": "Per charge month, return succeeded charges, disputed charges (attributed to the charge's month), and the dispute rate. State the late-arrival caveat.",
    "deliverable": "One row per charge month: charge_month, succeeded, disputed, dispute_rate.",
    "why": "Choosing the right timestamp and flagging late-arriving data is exactly the senior judgment Stripe tests.",
    "harder": "You must bucket the dispute on the charge's date via a join, and reason about data maturity.",
    "before": [
      "A dispute's created_at is the filing date, not the charge date.",
      "'By charge month' means DATE_TRUNC('month', charges.created_at)."
    ],
    "howto": [
      "succeeded per charge month from charges.",
      "disputed per charge month: join disputes → charges, bucket on the charge's created_at.",
      "Join the two monthly rollups; guard the rate."
    ],
    "hints": [
      "Pre-aggregate disputes to charge grain or count distinct disputed charges to avoid fan-out.",
      "The newest months under-report because disputes arrive late — say so."
    ],
    "solution": "WITH succeeded AS (\n  SELECT DATE_TRUNC('month', created_at) AS charge_month,\n         COUNT(*) AS succeeded\n  FROM charges WHERE status='succeeded'\n  GROUP BY DATE_TRUNC('month', created_at)\n),\ndisputed AS (\n  SELECT DATE_TRUNC('month', c.created_at) AS charge_month,\n         COUNT(DISTINCT d.charge_id) AS disputed\n  FROM disputes d\n  JOIN charges c ON c.charge_id = d.charge_id AND c.status='succeeded'\n  GROUP BY DATE_TRUNC('month', c.created_at)\n)\nSELECT s.charge_month,\n       s.succeeded,\n       COALESCE(d.disputed,0) AS disputed,\n       ROUND(COALESCE(d.disputed,0)::numeric / NULLIF(s.succeeded,0), 4) AS dispute_rate\nFROM succeeded s\nLEFT JOIN disputed d USING (charge_month)\nORDER BY s.charge_month;",
    "verify": {
      "grain": "One row per charge month.",
      "columns": ["charge_month", "succeeded", "disputed", "dispute_rate"],
      "commonWrong": ["Bucketing on the dispute's created_at (misdates late disputes).", "Joining raw disputes and counting (fan-out)."],
      "validation": ["disputed ≤ succeeded per month", "dispute_rate ∈ [0,1]", "recent months flagged as provisional"],
      "edgeCases": ["A charge with two disputes counts once via COUNT(DISTINCT charge_id).", "The newest 1–2 months keep gaining disputes."],
      "checklist": ["bucketed on charge month", "distinct disputed charges", "guarded rate", "late-arrival caveat stated"]
    },
    "confusion": "The numerator's month must come from the CHARGE, not the dispute — that's the whole point.",
    "explain": "Justify the timestamp choice and the maturing-data caveat aloud.",
    "teaches": "Attributing late-arriving events to the correct source month.",
    "mode": "SQL"
  },
  {
    "id": "dt8",
    "ladder": "dt",
    "pos": 8,
    "stage": "Final boss",
    "lvl": 5,
    "difficulty": "final-boss",
    "priority": "boss",
    "source": "Stripe-interview-style",
    "module": "m7",
    "title": "Final boss (timed 10 min): rolling 30-day success rate, volume-floored",
    "concept": ["datetime"],
    "obj": ["Charge"],
    "metric": "success rate",
    "edge": ["calendar vs rolling", "volume floor", "time zone"],
    "timed": true,
    "est": "10 min",
    "business": "\"Which merchants have a weak success rate right now?\" Define the window, the floor, and your assumptions.",
    "schema": ["charges"],
    "prompt": "Per merchant, the success rate over a rolling 30-day window, only for merchants with at least 200 attempts in that window. State whether you chose rolling or calendar and your time-zone assumption.",
    "deliverable": "One row per qualifying merchant: merchant_id, attempts, succeeded, success_rate.",
    "why": "This fuses window choice, bucketing-free aggregation, a volume floor and an explicit assumption — a complete time-metric interview answer.",
    "harder": "Combines rolling-window discipline, a HAVING floor, a guarded rate and a stated time-zone caveat under time pressure.",
    "before": [
      "Rolling vs calendar: 'right now' argues for a rolling 30-day window.",
      "The floor belongs in HAVING, not a WHERE that hides small merchants from the analysis intent.",
      "Timestamps are UTC; a merchant-local report would shift the window edges."
    ],
    "howto": [
      "Filter the rolling 30-day window.",
      "Aggregate attempts and succeeded per merchant.",
      "HAVING attempts >= 200; ROUND the guarded rate."
    ],
    "hints": [
      "created_at >= NOW() - INTERVAL '30 days' is the rolling window (no DATE_TRUNC needed here).",
      "succeeded = COUNT(*) FILTER (WHERE status='succeeded'); guard with NULLIF.",
      "Put the 200 floor in HAVING."
    ],
    "solution": "SELECT merchant_id,\n       COUNT(*) AS attempts,\n       COUNT(*) FILTER (WHERE status='succeeded') AS succeeded,\n       ROUND(COUNT(*) FILTER (WHERE status='succeeded')::numeric\n             / NULLIF(COUNT(*),0), 4) AS success_rate\nFROM charges\nWHERE created_at >= NOW() - INTERVAL '30 days'\nGROUP BY merchant_id\nHAVING COUNT(*) >= 200\nORDER BY success_rate;",
    "rubric": [
      "Chose and stated rolling (not calendar) for 'right now'",
      "Rolling 30-day window on created_at",
      "Volume floor (≥200) in HAVING, not WHERE",
      "Success rate guarded with NULLIF and cast to numeric",
      "Time-zone assumption stated (timestamps are UTC)"
    ],
    "verify": {
      "grain": "One row per merchant above the volume floor.",
      "columns": ["merchant_id", "attempts", "succeeded", "success_rate"],
      "sample": { "cols": ["merchant_id", "attempts", "succeeded", "success_rate"], "rows": [["108", "640", "467", "0.7297"], ["101", "412", "395", "0.9587"]] },
      "commonWrong": ["Using DATE_TRUNC('month') (calendar) for a 'right now' question.", "Putting the floor in WHERE on a non-aggregate.", "Integer division of succeeded/attempts."],
      "validation": ["success_rate ∈ [0,1]", "every row has attempts ≥ 200", "succeeded ≤ attempts"],
      "edgeCases": ["A merchant at exactly 200 attempts is included.", "UTC vs merchant-local shifts which charges fall in the window."],
      "checklist": ["rolling window", "HAVING floor", "guarded rate", "time-zone caveat"]
    },
    "confusion": "'Right now' is rolling; reserve DATE_TRUNC for explicitly calendar questions.",
    "explain": "Defend rolling-vs-calendar, the floor placement, and the UTC assumption as if to an interviewer.",
    "teaches": "A complete, assumption-stated rolling-window health metric.",
    "mode": "SQL"
  }
];
